import type { FormFieldDefinitionModel } from './form-types';

/**
 * PD-16687 — how a form's questions are arranged on the page: sections with an optional
 * title and description, rows whose questions sit full, half or third width, and
 * display-only text between them. Presentation only: answers, validation, actions,
 * matching, notifications and exports stay keyed by field and never read the layout.
 *
 * Saved on each form version next to its fields (`form_version.layout`), so an old
 * submission can still be drawn the way its submitter saw it. A form with no layout is
 * drawn one question per row in field order, as before.
 */

/** Bumped when the stored shape changes, so older saved layouts can still be read. */
export const FORM_LAYOUT_VERSION = 1;

/** A row is one, two or three columns wide; a cell takes one to three of them. */
export const FORM_LAYOUT_COLUMN_COUNTS = [1, 2, 3] as const;

export type FormLayoutColumnCount = (typeof FORM_LAYOUT_COLUMN_COUNTS)[number];

export const FORM_LAYOUT_TEXT_STYLES = ['heading', 'paragraph'] as const;

export type FormLayoutTextStyle = (typeof FORM_LAYOUT_TEXT_STYLES)[number];

/** Section, row and text-block ids: generated by the editor, unique within a layout. */
export const FORM_LAYOUT_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

export const FORM_LAYOUT_LIMITS = {
	sectionTitle: 200,
	sectionDescription: 1000,
	text: 2000,
} as const;

/**
 * Choice questions, which can list their choices in columns. An account-manager question
 * (PD-16691) is a single choice like a dropdown, so it can be listed as radio buttons too.
 */
export const FORM_LAYOUT_CHOICE_TYPES: ReadonlyArray<string> = [
	'select',
	'multiselect',
	'teamMember',
];

export interface FormLayoutFieldCellModel {
	kind: 'field';
	fieldKey: string;
	/** Columns of the row the question takes; one when left out. */
	span?: FormLayoutColumnCount;
	/**
	 * Choice questions only: list the choices (radio buttons for one answer, checkboxes for
	 * several) in this many columns. Left out, a single-answer question is a dropdown and a
	 * multiple-answer one follows the renderer's default.
	 */
	optionColumns?: FormLayoutColumnCount;
}

export interface FormLayoutTextCellModel {
	kind: 'text';
	id: string;
	style: FormLayoutTextStyle;
	text: string;
	/** Columns of the row the text takes; one when left out. */
	span?: FormLayoutColumnCount;
}

export type FormLayoutCellModel =
	| FormLayoutFieldCellModel
	| FormLayoutTextCellModel;

/** Cells fill the row's columns left to right; columns they do not take stay empty. */
export interface FormLayoutRowModel {
	id: string;
	columns: FormLayoutColumnCount;
	cells: Array<FormLayoutCellModel>;
}

export interface FormLayoutSectionModel {
	id: string;
	title: string | null;
	description: string | null;
	rows: Array<FormLayoutRowModel>;
}

export interface FormLayoutModel {
	version: typeof FORM_LAYOUT_VERSION;
	sections: Array<FormLayoutSectionModel>;
}

/** The field keys a layout places, in the order it shows them. */
export function formLayoutFieldKeys(
	layout: FormLayoutModel | null | undefined,
): Array<string> {
	if (layout == null) {
		return [];
	}
	return layout.sections.flatMap((section) =>
		section.rows.flatMap((row) =>
			row.cells.flatMap((cell) =>
				cell.kind === 'field' ? [cell.fieldKey] : [],
			),
		),
	);
}

/**
 * The layout brought in line with the fields being saved, so a change on the Fields tab
 * never fails because of the layout: a question that was removed (or is now hidden, which
 * is never shown) leaves its cell, a question that is no longer a choice question loses
 * its choice columns, and a row left with nothing in it goes. Everything else is kept for
 * the definition check to judge.
 */
export function pruneFormLayout(
	layout: FormLayoutModel | null,
	fields: ReadonlyArray<
		Pick<FormFieldDefinitionModel, 'fieldKey' | 'fieldType'>
	>,
): FormLayoutModel | null {
	if (layout == null) {
		return null;
	}
	const typeByKey = new Map(
		fields.map((field) => [field.fieldKey, field.fieldType]),
	);
	return {
		...layout,
		sections: layout.sections.map((section) => ({
			...section,
			rows: section.rows
				.map((row) => ({
					...row,
					cells: row.cells.flatMap((cell): Array<FormLayoutCellModel> => {
						if (cell.kind !== 'field') {
							return [cell];
						}
						const fieldType = typeByKey.get(cell.fieldKey);
						if (fieldType == null || fieldType === 'hidden') {
							return [];
						}
						if (
							cell.optionColumns != null &&
							!FORM_LAYOUT_CHOICE_TYPES.includes(fieldType)
						) {
							return [
								{
									kind: 'field',
									fieldKey: cell.fieldKey,
									...(cell.span != null ? { span: cell.span } : {}),
								},
							];
						}
						return [cell];
					}),
				}))
				.filter((row) => row.cells.length > 0),
		})),
	};
}
